import { useMemo, useState } from 'react';
import { saveFeedback } from '@api/save-feedback';
import { HexalInstance } from '@common/enums';
import { Feedback, IMessage, Modals, ReferenceType } from '@common/types';
import { Button } from '@components/ui/button';
import { Pages } from '@constants/pages';
import useIsMobile from '@hooks/use-is-mobile';
import { cn, CURRENT_INSTANCE } from '@lib/utils';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import AnswerMessageContainer from './answer-message-container';
import FeedbackPanel from './feedback-panel';
import MarkdownResponse from './markdown-response';
import SourcesDropdown from './sources-dropdown';

interface ICompleteResponseProps {
  message: IMessage;
  onShowMoreAnswers: (messageId: number) => void;
  showModal: (type: Modals, userQuery?: string) => void;
  hasMoreOptions: boolean | undefined;
  userQuery?: string;
}

function CompleteResponse(props: ICompleteResponseProps) {
  const { t } = useTranslation();
  const isMobile = useIsMobile();
  const [feedbackSent, setFeedbackSent] = useState<boolean>(false);

  async function handleFeedback(feedback: Feedback) {
    if (props.message.queryId && props.message.questionId) {
      await saveFeedback({
        queryId: props.message.queryId,
        questionId: props.message.questionId,
        value: feedback,
      });
      setFeedbackSent(true);
    }
  }

  const textMessage = props.message.message || '';
  const hasReferenceLinks = useMemo(() => {
    const uniqueReferences = Array.from(
      new Map(
        (props.message.references || []).map((item) => [item.sourceDocumentId, item])
      ).values()
    );

    return uniqueReferences.some((ref) => ref.externalDocumentLink.trim().length > 0);
  }, [props.message.references]);

  const references = useMemo(() => {
    return (props.message.references || []).map((ref, index) => {
      const { name, sourceDocumentId, headerId, headerTitle, referenceType, externalDocumentLink } =
        ref;
      let path = `${Pages.PDFViewer}/${sourceDocumentId}`;

      if (ReferenceType.Web === referenceType) {
        path = externalDocumentLink;
      } else if ([ReferenceType.Smpc, ReferenceType.Leaflet].includes(referenceType)) {
        path = `${path}?header-id=${headerId}`;
      }

      const linkName = `${[ReferenceType.Smpc, ReferenceType.Leaflet].includes(referenceType) ? t(`common.${referenceType}`) + ': ' : ''}${name}${headerTitle ? `, ${headerTitle}` : ''}`;

      return (
        <div key={index}>
          <Link to={path} target={ReferenceType.Web === referenceType ? '_blank' : '_self'}>
            {linkName}
          </Link>
        </div>
      );
    });
  }, [props.message, t]);

  return (
    <div className="mb-5">
      <AnswerMessageContainer>
        {props.message.questionQuery && (
          <div>
            <i>
              <p>
                {t('chat.response.found_answer_for', {
                  found_question: props.message.questionQuery,
                })}
              </p>
            </i>
          </div>
        )}

        <MarkdownResponse message={textMessage} />
        <div className="mt-4 space-y-0.5">{references}</div>
        <footer className="flex justify-between items-center mt-5">
          <div />
          <section className="flex justify-between items-center gap-6">
            {props.hasMoreOptions && (
              <Button
                className={cn(
                  'bg-xircle-main text-xs hover:bg-xircle-secondary alternative-buttons',
                  CURRENT_INSTANCE.name === HexalInstance.Default &&
                    'hover:text-hexal-dark-text-color'
                )}
                onClick={() => props.onShowMoreAnswers(props.message.id)}
              >
                {t('chat.alternative_answers')}
              </Button>
            )}
            {!feedbackSent && <FeedbackPanel submit={handleFeedback} />}
          </section>
        </footer>
      </AnswerMessageContainer>
      <section
        className={cn(
          'w-full flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-3 sm:px-5',
          hasReferenceLinks ? 'sm:w-3/5' : 'sm:w-fit'
        )}
      >
        <Button
          className={cn(
            'bg-xircle-secondary w-full follow-up-buttons',
            !hasReferenceLinks && 'sm:w-auto',
            CURRENT_INSTANCE.name === HexalInstance.Default &&
              'text-hexal-dark-text-color hover:text-slate-200'
          )}
          size={isMobile ? 'sm' : 'default'}
          onClick={() => props.showModal(Modals.MedicalInfo, props.userQuery)}
        >
          {t('chat.info_service')}
        </Button>
        <SourcesDropdown references={props.message.references} />
      </section>
    </div>
  );
}

export default CompleteResponse;
